import { ApplicationStatusEnum, SocialChannelEnum } from '../../../../common/general-types/briefs.dto';
import type { CreatorResponseDto } from '../../../../common/general-types/creators.dto';
import type { UltraSocialSocialAccount } from '../../../../services/ultra-social/ultra-social';

export type ProfilePlatform = 'tiktok' | 'instagram' | 'youtube' | 'snapchat';

export type ProfilePrimaryAction = 'hire' | 'collaborate' | 'unselect' | 'none';

export interface CreatorProfileReview {
  review?: string;
  createdDate?: string | Date;
  wouldRecommend?: boolean;
  communicationRating?: number;
  professionalismRating?: number;
  timeManagementRating?: number;
  workQualityRating?: number;
  application?: {
    brief?: {
      title?: string;
      businessAccount?: {
        companyName?: string;
        companyLogo?: string;
      };
    };
  };
}

export interface ReviewListState {
  items: CreatorProfileReview[];
  page: number;
  count: number | undefined;
  loading: boolean;
  error: boolean;
}

export interface ConnectedAccountDraft {
  platform: ProfilePlatform;
  label: string;
  connected: boolean;
  handle: string | null;
  followers: number | null;
  engagement: number | null;
  profileUrl: string | null;
  iconSrc: string;
}

export interface PortfolioBrandSource {
  brief?: {
    businessAccount?: { companyName?: string | null } | null;
    shoutBrief?: { socialPlatform?: SocialChannelEnum | null } | null;
  } | null;
}

export interface DeliveryAddressViewer {
  creator?: { blazeFirebaseUid?: string } | null;
  applicationStatus?: ApplicationStatusEnum;
}

const ADDRESS_VISIBLE_STATUSES = new Set<ApplicationStatusEnum>([
  ApplicationStatusEnum.Hired,
  ApplicationStatusEnum.ReadyToPost,
  ApplicationStatusEnum.Posted,
  ApplicationStatusEnum.Started,
]);

const PLATFORM_META: Record<ProfilePlatform, { label: string; iconSrc: string }> = {
  tiktok: { label: 'TikTok', iconSrc: 'assets/social-icon/tik-tok-transparent.svg' },
  instagram: { label: 'Instagram', iconSrc: 'assets/social-icon/instagram-logo.png' },
  youtube: { label: 'YouTube', iconSrc: 'assets/social-icon/youtube_shorts.webp' },
  snapchat: { label: 'Snapchat', iconSrc: 'assets/social-icon/snapchat-logo.svg' },
};

export function initialReviewListState(): ReviewListState {
  return { items: [], page: 0, count: undefined, loading: false, error: false };
}

export function socialPanelKey(creatorId: string | undefined, ultraCreatorId: string | undefined): string {
  return `${creatorId ?? ''}:${ultraCreatorId ?? 'none'}`;
}

export function isCreatorResponseCurrent(requestCreatorId: string, activeCreatorId: string | undefined): boolean {
  return requestCreatorId === activeCreatorId;
}

export function startReviewRequest(state: ReviewListState): ReviewListState {
  return { ...state, loading: true, error: false };
}

export function appendReviewPage(
  state: ReviewListState,
  data: CreatorProfileReview[],
  count: number,
): ReviewListState {
  const items = [...state.items, ...data];
  const resolvedCount = data.length === 0 ? items.length : count;
  return { ...state, items, count: resolvedCount, loading: false, error: false };
}

export function failReviewPage(state: ReviewListState): ReviewListState {
  return { ...state, loading: false, error: true };
}

export function advanceReviewPage(state: ReviewListState): ReviewListState | null {
  if (state.loading || state.error) {
    return null;
  }
  if (state.count == null || state.items.length >= state.count) {
    return null;
  }
  return { ...state, page: state.page + 1 };
}

export function totalReach(creator: CreatorResponseDto): number {
  return (
    (creator.tikTokFollowers || 0) +
    (creator.instaFollowers || 0) +
    (creator.snapchatFollowers || 0) +
    (creator.youtubeSubscribers || 0)
  );
}

export function averageEngagement(creator: CreatorResponseDto): number | null {
  const social = creator.ssCreator;
  if (!social) {
    return null;
  }
  const tiktok = social.tiktokAverages?.averageEngagementRate;
  const instagram = social.instagramAverages?.averageEngagementRate;
  if (tiktok != null && instagram != null) {
    return (tiktok + instagram) / 2;
  }
  if (instagram != null) {
    return instagram;
  }
  if (tiktok != null) {
    return tiktok;
  }
  return null;
}

export function reviewHeadline(
  creator: CreatorResponseDto | undefined,
  loadedCount: number | undefined,
): { rating: number | null; count: number | null } {
  if (!creator) {
    return { rating: null, count: null };
  }
  const summary = creator.reviews_summary;
  const summaryRating = summary?.average_rating;
  const rating = summaryRating != null && summaryRating > 0
    ? summaryRating
    : creator.rating > 0
      ? creator.rating
      : null;
  const count = summary?.total_reviews ?? loadedCount ?? null;
  return { rating, count };
}

export function reviewSubScores(reviews: CreatorProfileReview[]): {
  communication: number;
  professionalism: number;
  timeliness: number;
  quality: number;
} | null {
  if (!reviews.length) {
    return null;
  }
  const communication = averageOf(reviews, (review) => review.communicationRating);
  const professionalism = averageOf(reviews, (review) => review.professionalismRating);
  const timeliness = averageOf(reviews, (review) => review.timeManagementRating);
  const quality = averageOf(reviews, (review) => review.workQualityRating);
  if (
    communication == null ||
    professionalism == null ||
    timeliness == null ||
    quality == null
  ) {
    return null;
  }
  return { communication, professionalism, timeliness, quality };
}

export function rehirePercent(reviews: CreatorProfileReview[]): number | null {
  if (!reviews.length) {
    return null;
  }
  const rehired = reviews.filter((review) => review.wouldRecommend).length;
  return (rehired / reviews.length) * 100;
}

export function reviewOverall(review: CreatorProfileReview): number | null {
  return mean([
    review.communicationRating,
    review.professionalismRating,
    review.timeManagementRating,
    review.workQualityRating,
  ]);
}

export function formatScore(value: number | null): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1);
}

export function starGlyph(rating: number | null): string {
  if (rating == null) {
    return '☆☆☆☆☆';
  }
  const filled = Math.round(Math.min(5, Math.max(0, rating)));
  return `${'★'.repeat(filled)}${'☆'.repeat(5 - filled)}`;
}

export function barWidth(score: number): number {
  return Math.min(100, Math.max(0, (score / 5) * 100));
}

export function buildConnectedAccounts(input: {
  creator: CreatorResponseDto;
  tiktok?: UltraSocialSocialAccount;
  instagram?: UltraSocialSocialAccount;
  youtube?: UltraSocialSocialAccount;
  snapchat?: UltraSocialSocialAccount;
}): ConnectedAccountDraft[] {
  return [
    accountRow('tiktok', input.creator.tikTokHandle, input.creator.tikTokFollowers, input.tiktok, engagementFor(input.creator, 'tiktok')),
    accountRow('instagram', input.creator.instaHandle, input.creator.instaFollowers, input.instagram, engagementFor(input.creator, 'instagram')),
    accountRow('youtube', input.creator.youtubeHandle, input.creator.youtubeSubscribers, input.youtube, engagementFor(input.creator, 'youtube')),
    accountRow('snapchat', input.creator.snapchatHandle, input.creator.snapchatFollowers, input.snapchat, null),
  ];
}

export function portfolioBrandName(video: PortfolioBrandSource): string {
  if (!video.brief) {
    return 'Creator Upload';
  }
  const name = video.brief.businessAccount?.companyName?.trim();
  return name ? name : 'SHOUT';
}

export function portfolioBrandOptions(videos: PortfolioBrandSource[]): string[] {
  const names = new Set<string>();
  for (const video of videos) {
    names.add(portfolioBrandName(video));
  }
  return [...names];
}

export function filterPortfolioVideos<T extends PortfolioBrandSource>(videos: T[], brand: string): T[] {
  if (brand === 'All') {
    return videos;
  }
  return videos.filter((video) => portfolioBrandName(video) === brand);
}

export function portfolioPlatformLabel(platform: SocialChannelEnum | null | undefined): string | null {
  switch (platform) {
    case SocialChannelEnum['TikTok Post']:
      return 'TikTok';
    case SocialChannelEnum['Instagram Reel']:
    case SocialChannelEnum['Instagram Story']:
      return 'Instagram';
    case SocialChannelEnum['YouTube Shorts']:
      return 'YouTube';
    case SocialChannelEnum['Snapchat Spotlight']:
    case SocialChannelEnum['Snapchat Story']:
      return 'Snapchat';
    default:
      return null;
  }
}

export function resolvePrimaryAction(input: {
  showFooterActions: boolean;
  isManageBriefHireContext: boolean;
  canHireFromProfile: boolean;
  isSelected: boolean;
}): ProfilePrimaryAction {
  if (!input.showFooterActions) {
    return 'none';
  }
  if (input.isManageBriefHireContext) {
    return input.canHireFromProfile ? 'hire' : 'none';
  }
  return input.isSelected ? 'unselect' : 'collaborate';
}

export function canShowCreatorDeliveryAddress(
  application: DeliveryAddressViewer | null | undefined,
  creatorUid: string,
): boolean {
  if (
    !application ||
    application.creator?.blazeFirebaseUid !== creatorUid ||
    application.applicationStatus == null ||
    !ADDRESS_VISIBLE_STATUSES.has(application.applicationStatus)
  ) {
    return false;
  }
  return true;
}

export function brandMonogram(name: string | undefined): string {
  const trimmed = name?.trim();
  if (!trimmed) {
    return '?';
  }
  return trimmed.charAt(0).toUpperCase();
}

/** size-8 logo + gap-1.5 between items. */
const RECENT_BRAND_LOGO_PX = 32;
const RECENT_BRAND_GAP_PX = 6;
/** Reserved width for the "+ N more" pill so the row never wraps. */
const RECENT_BRAND_MORE_PILL_PX = 104;

/** How many brand logos fit on one row (with a "+ more" pill when not all fit). */
export function recentBrandsFitCount(containerWidth: number, totalLogos: number): number {
  if (containerWidth <= 0 || totalLogos <= 0) {
    return 0;
  }
  const allWidth =
    totalLogos * RECENT_BRAND_LOGO_PX + Math.max(0, totalLogos - 1) * RECENT_BRAND_GAP_PX;
  if (allWidth <= containerWidth) {
    return totalLogos;
  }
  const slot = RECENT_BRAND_LOGO_PX + RECENT_BRAND_GAP_PX;
  const fitWithPill = Math.floor((containerWidth - RECENT_BRAND_MORE_PILL_PX) / slot);
  return Math.max(1, Math.min(totalLogos - 1, fitWithPill));
}

function accountRow(
  platform: ProfilePlatform,
  creatorHandle: string | undefined,
  storedFollowers: number | undefined,
  ultra: UltraSocialSocialAccount | undefined,
  engagement: number | null,
): ConnectedAccountDraft {
  const handle = cleanHandle(creatorHandle) ?? cleanHandle(ultra?.handle);
  const connected = !!handle || !!ultra;
  const followers = ultra?.followerCount != null
    ? ultra.followerCount
    : connected
      ? (storedFollowers ?? null)
      : null;
  return {
    platform,
    label: PLATFORM_META[platform].label,
    connected,
    handle,
    followers,
    engagement: connected ? engagement : null,
    profileUrl: handle ? profileUrl(platform, handle) : null,
    iconSrc: PLATFORM_META[platform].iconSrc,
  };
}

function engagementFor(creator: CreatorResponseDto, platform: 'tiktok' | 'instagram' | 'youtube'): number | null {
  const social = creator.ssCreator;
  if (!social) {
    return null;
  }
  if (platform === 'tiktok') {
    return social.tiktokAverages?.averageEngagementRate ?? null;
  }
  if (platform === 'instagram') {
    return social.instagramAverages?.averageEngagementRate ?? null;
  }
  return social.youtubeAverages?.averageEngagementRate ?? null;
}

function cleanHandle(handle: string | undefined | null): string | null {
  const trimmed = handle?.trim().replace(/^@/, '');
  return trimmed ? trimmed : null;
}

function profileUrl(platform: ProfilePlatform, handle: string): string {
  const encoded = encodeURIComponent(handle);
  switch (platform) {
    case 'tiktok':
      return `https://www.tiktok.com/@${encoded}`;
    case 'instagram':
      return `https://www.instagram.com/${encoded}`;
    case 'youtube':
      return `https://www.youtube.com/@${encoded}`;
    case 'snapchat':
      return `https://www.snapchat.com/add/${encoded}`;
  }
}

function averageOf(
  reviews: CreatorProfileReview[],
  pick: (review: CreatorProfileReview) => number | undefined,
): number | null {
  return mean(reviews.map(pick));
}

function mean(values: Array<number | undefined>): number | null {
  const numbers = values.filter((value): value is number => typeof value === 'number');
  if (!numbers.length) {
    return null;
  }
  return numbers.reduce((sum, value) => sum + value, 0) / numbers.length;
}
